import {LanguageDescription, LanguageSupport} from '@codemirror/language';

type DescriptionSpec = Parameters<(typeof LanguageDescription)['of']>[0];

async function buildHtmlSupport() {
  const [htmlMod, cssMod, jsMod] = await Promise.all([
    import('@codemirror/lang-html'),
    import('@codemirror/lang-css'),
    import('@codemirror/lang-javascript'),
  ]);
  return new LanguageSupport(htmlMod.htmlLanguage, [
    htmlMod.htmlLanguage.data.of({
      autocomplete: htmlMod.htmlCompletionSource,
    }),
    htmlMod.autoCloseTags,
    // CSS language for <style> parsing — WITHOUT built-in completions
    new LanguageSupport(cssMod.cssLanguage),
    // JS language with standard completions
    jsMod.javascript().support,
  ]);
}

const languageSpecs = {
  CSS: {
    name: 'CSS',
    extensions: ['css'],
    // Use cssLanguage directly (not css()) to exclude the built-in flat
    // value completions. Our custom cssCompletionSource in
    // extensions/completions/ provides property-aware value completions
    // plus property names, pseudo-classes, at-rules, and tags.
    load: async () =>
      import('@codemirror/lang-css').then(
        (m) => new LanguageSupport(m.cssLanguage)
      ),
  },
  HTML: {
    name: 'HTML',
    alias: ['xhtml', 'htmlbars'],
    extensions: ['html', 'htm', 'handlebars', 'hbs', 'svg'],
    // Build HTML support manually to exclude the built-in CSS value
    // completions that html() bundles via css(). The built-in source
    // returns a flat list of ALL CSS keywords (fantasy, firebrick, etc.)
    // regardless of property context. Our property-aware cssCompletionSource
    // (registered in extensions/completions/) replaces it with spec-accurate
    // values per property.
    load: buildHtmlSupport,
  },
  JavaScript: {
    name: 'JavaScript',
    alias: ['ecmascript', 'js', 'node'],
    extensions: ['js', 'mjs', 'cjs'],
    load: async () =>
      import('@codemirror/lang-javascript').then((m) => m.javascript()),
  },
  JSX: {
    name: 'JSX',
    extensions: ['jsx'],
    load: async () =>
      import('@codemirror/lang-javascript').then((m) =>
        m.javascript({jsx: true})
      ),
  },
  JSON: {
    name: 'JSON',
    alias: ['json5'],
    extensions: ['json', 'jsonc', 'map'],
    load: async () => import('../languages/jsonc').then((m) => m.jsonc()),
  },
  TypeScript: {
    name: 'TypeScript',
    alias: ['ts'],
    extensions: ['ts', 'mts', 'cts'],
    load: async () =>
      import('@codemirror/lang-javascript').then((m) =>
        m.javascript({typescript: true})
      ),
  },
  TSX: {
    name: 'TSX',
    extensions: ['tsx', 'astro'],
    load: async () =>
      import('@codemirror/lang-javascript').then((m) =>
        m.javascript({jsx: true, typescript: true})
      ),
  },
  ENV: {
    name: 'ENV',
    alias: ['dotenv'],
    extensions: ['env', 'npmrc', 'ini'],
    load: async () => import('../languages/env').then((m) => m.env()),
  },
} satisfies Record<string, DescriptionSpec>;

export type LanguageName = keyof typeof languageSpecs;

export const languages = Object.values(languageSpecs).map(
  LanguageDescription.of
);
